import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { formatPct, formatQty, marginPct, matchesSearchTerms, searchTerms, stockStatus, variantLabel, UNIT_TYPES, type UnitType } from '@pos/core';
import { applyLocalChanges, type LocalChange, type VariantRow, type VariantStock } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { productImageUrl } from '../../lib/images.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Badge, Button, Card, Input, PageHeader, Select, Spinner, errorMessage } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { MoneyInput } from '../../ui/MoneyInput.tsx';
import { priceRange, useCatalog, useMoney, type CatalogItem } from './data.ts';
import { useStockMap } from '../stock/data.ts';

const PAGE = 100;

export function ProductsPage() {
  const { can } = useSession();
  const catalog = useCatalog();
  const stock = useStockMap();
  const money = useMoney();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'all'>('active');
  const [limit, setLimit] = useState(PAGE);
  const [priceItem, setPriceItem] = useState<CatalogItem | null>(null);

  const manage = can('manage_products');
  const editPrices = can('edit_prices');
  const showCosts = can('view_costs');

  const filtered = useMemo(() => {
    if (!catalog) return [];
    const terms = searchTerms(query);
    return catalog.items.filter(
      (i) =>
        (status === 'all' || (status === 'active') === i.product.active) &&
        (!category || (category === 'none' ? !i.category : i.category?.id === category)) &&
        matchesSearchTerms(i.search, terms),
    );
  }, [catalog, query, category, status]);

  if (!catalog) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  const open = (item: CatalogItem) => {
    if (manage) navigate(`/productos/${item.product.id}`);
    else if (editPrices) setPriceItem(item);
  };

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Productos"
        description={`${catalog.items.length} producto${catalog.items.length === 1 ? '' : 's'}`}
        actions={
          manage && (
            <>
              <Link to="/productos/importar">
                <Button variant="secondary">Importar</Button>
              </Link>
              <Link to="/productos/nuevo">
                <Button>Nuevo producto</Button>
              </Link>
            </>
          )
        }
      />

      {manage && (
        <div className="-mt-3 mb-5 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <Link className="font-medium text-brand hover:underline" to="/productos/aumento">
            Aumento masivo de precios
          </Link>
          <Link className="font-medium text-brand hover:underline" to="/productos/categorias">
            Categorías
          </Link>
          <Link className="font-medium text-brand hover:underline" to="/productos/proveedores">
            Proveedores
          </Link>
        </div>
      )}

      {catalog.items.length === 0 ? (
        <Card className="text-center">
          <h2 className="font-semibold">Todavía no hay productos</h2>
          {manage ? (
            <>
              <p className="mt-1 text-sm text-slate-500">Cargalos de a uno o importá tu lista desde Excel.</p>
              <div className="mt-4 flex justify-center gap-2">
                <Link to="/productos/importar">
                  <Button variant="secondary">Importar desde Excel</Button>
                </Link>
                <Link to="/productos/nuevo">
                  <Button>Cargar el primero</Button>
                </Link>
              </div>
            </>
          ) : (
            <p className="mt-1 text-sm text-slate-500">El dueño todavía no cargó productos.</p>
          )}
        </Card>
      ) : (
        <>
          <div className="mb-4 grid gap-2 sm:grid-cols-[1fr_auto_auto]">
            <Input
              type="search"
              placeholder="Buscar por nombre, código o categoría…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setLimit(PAGE);
              }}
              aria-label="Buscar productos"
            />
            <Select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Categoría">
              <option value="">Todas las categorías</option>
              {catalog.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              <option value="none">Sin categoría</option>
            </Select>
            <Select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} aria-label="Estado">
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
              <option value="all">Todos</option>
            </Select>
          </div>

          {!manage && editPrices && (
            <p className="mb-3 text-sm text-slate-500">Tocá un producto para cambiarle el precio.</p>
          )}

          <Card flush className="divide-y divide-slate-100">
            {filtered.length === 0 && <p className="p-6 text-center text-sm text-slate-500">No hay productos que coincidan.</p>}
            {filtered.slice(0, limit).map((item) => (
              <ProductItem
                key={item.product.id}
                item={item}
                money={money}
                costs={showCosts ? catalog.costs : null}
                stock={stock}
                clickable={manage || editPrices}
                onOpen={() => open(item)}
              />
            ))}
          </Card>
          {filtered.length > limit && (
            <div className="mt-4 text-center">
              <Button variant="secondary" onClick={() => setLimit((l) => l + PAGE)}>
                Mostrar más ({filtered.length - limit} restantes)
              </Button>
            </div>
          )}
        </>
      )}

      <PriceDialog item={priceItem} onClose={() => setPriceItem(null)} />
    </div>
  );
}

function ProductItem({
  item,
  money,
  costs,
  stock,
  clickable,
  onOpen,
}: {
  item: CatalogItem;
  money: (c: number) => string;
  costs: Map<string, number> | null;
  stock: Map<string, VariantStock> | undefined;
  clickable: boolean;
  onOpen: () => void;
}) {
  const { product, variants, category } = item;
  const image = productImageUrl(product.image_path);
  const main = variants[0];
  const cost = main && costs ? costs.get(main.id) : undefined;
  const detail = [
    category?.name,
    product.has_variants ? `${variants.length} variantes` : main?.barcode,
    product.unit_type !== 'unit' ? `por ${UNIT_TYPES[product.unit_type].symbol}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const content = (
    <>
      {image ? (
        <img src={image} alt="" loading="lazy" className="size-12 shrink-0 rounded-lg bg-slate-100 object-cover" />
      ) : (
        <span className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-lg font-semibold text-slate-500">
          {product.name.charAt(0).toUpperCase()}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{product.name}</p>
        <p className="truncate text-sm text-slate-500">{detail || 'Sin categoría'}</p>
        <StockLine variants={variants} stock={stock} unit={product.unit_type} />
      </div>
      <div className="shrink-0 text-right">
        <p className="font-semibold">{priceRange(variants, money)}</p>
        {costs && !product.has_variants && (
          <p className="text-xs text-slate-500">
            {cost === undefined ? 'Sin costo' : `Costo ${money(cost)} · margen ${formatPct(marginPct(cost, main!.price), 0)}`}
          </p>
        )}
        {!product.active && <Badge tone="slate">Inactivo</Badge>}
      </div>
    </>
  );

  return clickable ? (
    <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 p-3 text-left hover:bg-slate-50 sm:p-4">
      {content}
    </button>
  ) : (
    <div className="flex items-center gap-3 p-3 sm:p-4">{content}</div>
  );
}

/** Cambio de precio para empleados con el permiso "editar precios". */
function PriceDialog({ item, onClose }: { item: CatalogItem | null; onClose: () => void }) {
  const { writer } = useSession();
  const [prices, setPrices] = useState<Record<string, number | null>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const value = (id: string, fallback: number) => (id in prices ? prices[id]! : fallback);

  const save = async () => {
    if (!item) return;
    const changes: LocalChange[] = [];
    for (const v of item.variants) {
      const p = value(v.id, v.price);
      if (p === null || p < 0) return setError('Revisá los precios: hay alguno vacío o inválido.');
      if (p !== v.price) changes.push({ table: 'product_variants', kind: 'patch', id: v.id, changes: { price: p } });
    }
    setBusy(true);
    try {
      await applyLocalChanges(db, changes, writer);
      requestSync(0);
      setPrices({});
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!item}
      onClose={() => {
        setPrices({});
        setError(null);
        onClose();
      }}
      title={item ? `Precio de ${item.product.name}` : ''}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={save} loading={busy}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <Alert tone="error">{error}</Alert>}
        {item?.variants.map((v) => (
          <label key={v.id} className="block space-y-1">
            <span className="text-sm font-medium text-slate-700">{variantLabel(v.attributes) || 'Precio'}</span>
            <MoneyInput value={value(v.id, v.price)} onChange={(c) => setPrices((p) => ({ ...p, [v.id]: c }))} />
          </label>
        ))}
      </div>
    </Dialog>
  );
}

/** Stock del producto en la lista: total de sus variantes, con color si hay alerta. */
function StockLine({ variants, stock, unit }: { variants: VariantRow[]; stock: Map<string, VariantStock> | undefined; unit: UnitType }) {
  if (!stock) return null;
  const tracked = variants.filter((v) => stock.get(v.id)?.tracked);
  if (tracked.length === 0) return <p className="text-xs text-slate-400">Sin control de stock</p>;
  const total = tracked.reduce((sum, v) => sum + stock.get(v.id)!.qty, 0);
  const alert = tracked.some((v) => {
    const st = stockStatus(stock.get(v.id)!.qty, v.min_stock, true);
    return st !== 'ok';
  });
  return <p className={alert ? 'text-xs font-medium text-amber-700' : 'text-xs text-slate-500'}>Stock: {formatQty(total, unit)}{alert ? ' · revisar' : ''}</p>;
}
